/**
 * Safe value extraction utilities for handling ES data that may have nested value objects.
 *
 * ES data sometimes comes back with fields wrapped in {value: ...} objects
 * instead of primitive values. These utilities safely extract the underlying values.
 */

/**
 * Safely extract a string value from a field that might be an object with {value: string}
 * This handles cases where ES returns data in different formats.
 *
 * @param value - The value to extract from, which could be a string, object with {value}, or other type
 * @returns The extracted string value, or empty string if null/undefined
 *
 * @example
 * safeString("hello") // "hello"
 * safeString({value: "hello"}) // "hello"
 * safeString(null) // ""
 * safeString(123) // "123"
 */
export const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "value" in (value as Record<string, unknown>)) {
    return safeString((value as Record<string, unknown>).value);
  }
  return String(value);
};

/**
 * Safely extract a number value from a field that might be an object with {value: number}
 *
 * @param value - The value to extract from
 * @param defaultValue - Default value if extraction fails (default: 0)
 * @returns The extracted number value
 *
 * @example
 * safeNumber(123) // 123
 * safeNumber({value: 123}) // 123
 * safeNumber(null) // 0
 */
export const safeNumber = (value: unknown, defaultValue = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === "number") return value;
  if (typeof value === "object" && "value" in (value as Record<string, unknown>)) {
    return safeNumber((value as Record<string, unknown>).value, defaultValue);
  }
  const parsed = Number(value);
  return isNaN(parsed) ? defaultValue : parsed;
};
